// What Dayoffs needs from the app that hosts it (#204), handed over once at
// bootstrap — the same pattern as core's registerHttpClients and
// datacentral-react's configureDataHooks. The host calls createDayoffsModule
// from src/app/register-dayoffs.tsx; nothing here imports the app.
//
// The generic half (modals, the session) is the shell's host contract; what is
// left is specific to this module. Every member is typed structurally, so the
// host's real objects are checked where it registers them, not here.
import type { ComponentType } from 'react'
import type { DateRangePreset } from '@vnetwork/datacentral-ui/primitives'
import type {
    HostLinks,
    HostModalApi,
    HostSession,
} from '@vnetwork/datacentral-shell/host'
import type { ExcelColumnMapping } from './types/excel'
import vi from './locales/vi.json'
import en from './locales/en.json'

/** The signed-in employee, as far as Dayoffs reads it. */
export interface DayoffsEmployee {
    employee_id: number
    employee_name: string
    position_name: string
    department_name: string
    employee_day_off_left: number
}

/** The props Dayoffs gives the host's Excel export button. */
export interface DayoffsExcelExportButtonProps {
    apiEndpoint: string
    columns: ExcelColumnMapping[]
    queryParams: Record<string, any>
    feature?: string
    variant?: 'secondary' | 'ghost'
    size?: 'sm' | 'md'
}

interface SelectOption {
    label: string
    value: any
}

/** The two entity lookups the filter bar reads (SimpleAsyncSelect/config). */
export interface DayoffsEntityConfig {
    fetchOptions: (
        query: string,
        params?: Record<string, any>
    ) => Promise<SelectOption[]>
    fetchOptionsWithPage: (
        query: string,
        page: number,
        params?: Record<string, any>
    ) => Promise<{ options: any[]; hasMore?: boolean }>
    fetchByIds: (
        ids: any[],
        params?: Record<string, any>
    ) => Promise<SelectOption[]>
}

export interface DayoffsHost
    extends HostModalApi,
        HostSession<DayoffsEmployee> {
    /**
     * The app's i18next instance, already initialised. The `dayoffs`
     * namespace is registered against it here. A package must not import
     * i18next itself: that would add bundles to whichever instance it got,
     * possibly before the app's init (#59 hazard 5).
     */
    i18n: {
        addResourceBundle: (
            lng: string,
            ns: string,
            resources: object
        ) => unknown
    }
    /** Pages of other modules the list and form breadcrumbs link to. */
    links: HostLinks<'account'>
    /** The app's error page (rendered inline by the update page). */
    ErrorPage: ComponentType<{ status: number }>
    /** The app's Excel export button (opens a column picker, downloads .xlsx). */
    ExcelExportButton: ComponentType<DayoffsExcelExportButtonProps>
    /** The app's entity lookups (department, employee). */
    getEntityConfig: (entity: 'department' | 'employee') => DayoffsEntityConfig
    /** The app's standard quick date ranges (today, last 7 days…). */
    dateRangePresets: DateRangePreset[]
}

let current: DayoffsHost | undefined

export function registerDayoffsHost(host: DayoffsHost): void {
    current = host
    host.i18n.addResourceBundle('vi', 'dayoffs', vi)
    host.i18n.addResourceBundle('en', 'dayoffs', en)
}

/** The registered host. Read it at render or call time, never at import. */
export function getDayoffsHost(): DayoffsHost {
    if (!current)
        throw new Error(
            '[dayoffs] no host registered. Call createDayoffsModule({ ...hostBase, i18n, links, … }) at app bootstrap (src/app/register-dayoffs.tsx).'
        )
    return current
}

// The host's hooks, under the names the app's own hooks have, so call sites
// read as before. Each reads the host at call time, inside a component.
export const useModal = () => getDayoffsHost().useModal()
export const useCurrentEmployee = () => getDayoffsHost().useCurrentEmployee()
export const useIsAdmin = () => getDayoffsHost().useIsAdmin()
